import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Eye, FolderPlus, Heart } from "lucide-react";
import client from "../api/client";
import { AddToProjectDialog, Layer } from "../Architect/common";
import { SOLD_AS } from "../Architect/quantities";
import { formatMoney, formatSize } from "../Architect/units";
import { capitalise } from "../utils/text";
import "../Moodboard/Moodboard.css";
import "../Architect/Architect.css";

/**
 * Full-resolution preview panel. Pixel dimensions and file size come from the
 * image itself; the like state is the persisted per-user favourite.
 */

const formatDimensions = (asset) =>
  asset?.width && asset?.height ? `${asset.width} x ${asset.height} px` : null;

const formatBytes = (value) => {
  const bytes = Number(value);
  if (!bytes || Number.isNaN(bytes)) return null;
  const units = ["B", "KB", "MB", "GB"];
  let size = bytes;
  let unit = 0;
  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024;
    unit += 1;
  }
  return `${size.toFixed(size >= 10 || unit === 0 ? 0 : 1)} ${units[unit]}`;
};

function ProductFacts({ details }) {
  const soldAs = SOLD_AS.find((entry) => entry.key === details.sold_as);
  const size =
    details.sold_as === "area"
      ? "Printed to the wall's size"
      : formatSize(details.width_mm, details.height_mm, "ft") &&
        `${details.sold_as === "roll" ? "Roll " : ""}${formatSize(details.width_mm, details.height_mm, "ft")} (${details.width_mm} × ${details.height_mm} mm)`;
  const facts = [
    ["Code", details.code],
    ["Size", size],
    ["Material", [details.material, details.finish].filter(Boolean).join(", ")],
    ["Price", details.price != null ? `INR ${formatMoney(details.price)} ${soldAs?.per || ""}` : "On request"],
  ].filter(([, value]) => value);
  return (
    <dl className="artwork-facts">
      {facts.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function ArtworkPreview({
  asset,
  imageUrl,
  onToggleFavorite,
  onDownload,
  showDownloadCount = false,
}) {
  const [details, setDetails] = useState(null);
  const [adding, setAdding] = useState(false);
  const [added, setAdded] = useState(null);
  const fileId = asset?.file_id;

  useEffect(() => {
    setDetails(null);
    setAdded(null);
    if (!fileId) return undefined;
    let live = true;
    client
      .get(`/api/products/${fileId}/details`)
      .then(({ data }) => live && setDetails(data))
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [fileId]);

  if (!asset) return null;

  const title = capitalise(asset.file_name) || "Untitled artwork";
  const liked = Boolean(asset.is_favorite);
  const meta = [formatDimensions(asset), formatBytes(asset.file_size)]
    .filter(Boolean)
    .join("  /  ");

  return (
    <div className="artwork-preview">
      <div className="artwork-preview-stage">
        {imageUrl ? (
          <img src={imageUrl} alt={title} className="artwork-preview-image" />
        ) : (
          <div className="artwork-preview-loading">Loading artwork...</div>
        )}
      </div>

      <aside className="artwork-preview-panel">
        <div>
          <p className="asset-ticket-label">{capitalise(asset.category)}</p>
          <h2>{title}</h2>
          {meta && <p className="artwork-preview-meta">{meta}</p>}

          <dl className="artwork-preview-stats">
            <div>
              <dt>
                <Eye size={14} strokeWidth={1.7} aria-hidden="true" /> Views
              </dt>
              <dd>{asset.views ?? 0}</dd>
            </div>
            {showDownloadCount && (
              <div>
                <dt>
                  <Download size={14} strokeWidth={1.7} aria-hidden="true" />{" "}
                  Downloads
                </dt>
                <dd>{asset.download_count ?? 0}</dd>
              </div>
            )}
          </dl>
          {details && <ProductFacts details={details} />}
        </div>

        <div className="artwork-preview-actions">
          <button
            type="button"
            className={`artwork-action-button ${liked ? "active" : ""}`}
            onClick={onToggleFavorite}
            aria-pressed={liked}
          >
            <Heart
              size={17}
              strokeWidth={1.7}
              fill={liked ? "currentColor" : "none"}
              aria-hidden="true"
            />
            <span>{liked ? "Favourited" : "Add to favourites"}</span>
          </button>
          <button
            type="button"
            className="artwork-action-button"
            onClick={onDownload}
            disabled={!asset.file_id}
          >
            <Download size={17} strokeWidth={1.7} aria-hidden="true" />
            <span>Download</span>
          </button>
          <button type="button" className="artwork-action-button" onClick={() => setAdding(true)} disabled={!asset.file_id}>
            <FolderPlus size={17} strokeWidth={1.7} aria-hidden="true" />
            <span>Add to project</span>
          </button>
          {added && (
            <p className="artwork-added">
              Added to <Link to={`/projects/${added.id}`}>{added.name}</Link>
            </p>
          )}
        </div>
        {adding && (
          <Layer>
            <AddToProjectDialog items={[{ file_id: asset.file_id }]} onClose={() => setAdding(false)} onAdded={setAdded} />
          </Layer>
        )}
      </aside>
    </div>
  );
}

export default ArtworkPreview;
